/**
 * Consent receipt: an auditable record of a single consent decision, persisted
 * client-side (localStorage) and server-side (Postgres) for the DPDP audit trail.
 */

import {
  CONSENT_RECEIPT_KEY,
  type ConsentCategories,
  type ConsentDecision,
} from "./consent-categories";

export interface ConsentReceipt {
  /** ULID — lexicographically sortable, timestamp-prefixed. */
  receiptId: string;
  timestamp: string;
  userAgent: string;
  policyVersion: string;
  categories: ConsentCategories;
  decision: ConsentDecision;
  legalBasis: "consent";
  withdrawalLink: string;
}

const ENCODING = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

/**
 * Generates a Crockford-base32 ULID (26 chars: 48-bit timestamp + 80-bit
 * randomness). Dependency-free; works in browser and Node. Uses crypto when
 * available, falling back to Math.random for unsupported environments.
 */
export function generateUlid(now: number = Date.now()): string {
  let time = now;
  const timeChars: string[] = [];
  for (let i = 9; i >= 0; i--) {
    timeChars[i] = ENCODING[time % 32];
    time = Math.floor(time / 32);
  }

  const random = new Uint8Array(16);
  const cryptoObj = typeof globalThis !== "undefined" ? globalThis.crypto : undefined;
  if (cryptoObj?.getRandomValues) {
    cryptoObj.getRandomValues(random);
  } else {
    for (let i = 0; i < random.length; i++) random[i] = Math.floor(Math.random() * 256);
  }
  const randChars: string[] = [];
  for (let i = 0; i < 16; i++) randChars.push(ENCODING[random[i] % 32]);

  return timeChars.join("") + randChars.join("");
}

export interface BuildReceiptInput {
  categories: ConsentCategories;
  decision: ConsentDecision;
  policyVersion: string;
  withdrawalLink: string;
  userAgent: string;
  now?: number;
}

export function buildConsentReceipt(input: BuildReceiptInput): ConsentReceipt {
  const now = input.now ?? Date.now();
  return {
    receiptId: generateUlid(now),
    timestamp: new Date(now).toISOString(),
    userAgent: input.userAgent,
    policyVersion: input.policyVersion,
    categories: input.categories,
    decision: input.decision,
    legalBasis: "consent",
    withdrawalLink: input.withdrawalLink,
  };
}

export function readReceipt(): ConsentReceipt | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_RECEIPT_KEY);
    return raw ? (JSON.parse(raw) as ConsentReceipt) : null;
  } catch {
    return null;
  }
}

export function persistReceipt(receipt: ConsentReceipt): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CONSENT_RECEIPT_KEY, JSON.stringify(receipt));
  } catch {
    // localStorage unavailable (private mode / quota) — server copy is the fallback.
  }
}

export function clearReceipt(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CONSENT_RECEIPT_KEY);
  } catch {
    // ignore
  }
}
